import React, { Component } from "react";
import PropTypes from "prop-types";

import GenericChartComponent from "../GenericChartComponent";
import { getAxisCanvas, getMouseCanvas } from "../GenericComponent";
import { hexToRGBA, getStrokeDasharray } from "../utils";

class SupportResistanceSeries extends Component {
  constructor(props) {
    super(props);
    this.yAccessorSupport = this.yAccessorSupport.bind(this);
    this.yAccessorResistance = this.yAccessorResistance.bind(this);
    this.renderSVG = this.renderSVG.bind(this);
    this.drawOnCanvas = this.drawOnCanvas.bind(this);
    this.getSegments = this.getSegments.bind(this);
    this.normalizeStroke = this.normalizeStroke.bind(this);
    this.normalizeFill = this.normalizeFill.bind(this);
    this.isHover = this.isHover.bind(this);
  }

  yAccessorSupport(d) {
    if (!d) return undefined;
    const { yAccessor } = this.props;
    const node = yAccessor ? yAccessor(d) : undefined;
    return node && node.support != null ? node.support : undefined;
  }

  yAccessorResistance(d) {
    if (!d) return undefined;
    const { yAccessor } = this.props;
    const node = yAccessor ? yAccessor(d) : undefined;
    return node && node.resistance != null ? node.resistance : undefined;
  }

  render() {
    const {
      className,
      clip,
      supportStroke,
      resistanceStroke,
      channelFillColor,
      channelFill = true, // Boolean option from config
      onContextMenu,
      onDoubleClick,
      onHover,
      onUnHover,
      onClick,
      highlightOnHover,
    } = this.props;

    const supportStrokeProps = this.normalizeStroke(supportStroke, {
      color: "#80c080",
      width: 1,
      dash: "Solid",
      opacity: 1,
    });

    const resistanceStrokeProps = this.normalizeStroke(resistanceStroke, {
      color: "#80c080",
      width: 1,
      dash: "Solid",
      opacity: 1,
    });

    // Only apply channel fill if enabled and channelFillColor is provided
    const channelFillProps =
      channelFill && channelFillColor
        ? this.normalizeFill(channelFillColor, {
            color: "#80c080",
            opacity: 0.2,
          })
        : { color: "#80c080", opacity: 0 }; // Disabled fill

    const needsHover =
      highlightOnHover ||
      onHover ||
      onUnHover ||
      onContextMenu ||
      onDoubleClick ||
      onClick;
    const hoverProps = needsHover
      ? {
          isHover: this.isHover,
          drawOn: ["mousemove", "pan"],
          canvasToDraw: getMouseCanvas,
        }
      : {
          drawOn: ["pan"],
          canvasToDraw: getAxisCanvas,
        };

    return (
      <g className={className}>
        <GenericChartComponent
          clip={clip}
          svgDraw={(moreProps) =>
            this.renderSVG(
              moreProps,
              supportStrokeProps,
              resistanceStrokeProps,
              channelFillProps,
            )
          }
          canvasDraw={(ctx, moreProps) =>
            this.drawOnCanvas(
              ctx,
              moreProps,
              supportStrokeProps,
              resistanceStrokeProps,
              channelFillProps,
            )
          }
          onHover={onHover}
          onUnHover={onUnHover}
          onClickWhenHover={onClick}
          onDoubleClickWhenHover={onDoubleClick}
          onContextMenuWhenHover={onContextMenu}
          {...hoverProps}
        />
      </g>
    );
  }
}

SupportResistanceSeries.prototype.normalizeStroke = function (
  stroke,
  fallback,
) {
  if (typeof stroke === "string") {
    return { ...fallback, color: stroke };
  }
  return {
    color: stroke?.color || fallback.color,
    width: stroke?.width ?? fallback.width,
    dash: stroke?.dash || fallback.dash,
    opacity: stroke?.opacity ?? fallback.opacity,
  };
};

SupportResistanceSeries.prototype.normalizeFill = function (fill, fallback) {
  if (typeof fill === "string") {
    return { ...fallback, color: fill };
  }
  return {
    color: fill?.color || fallback.color,
    opacity: fill?.opacity ?? fallback.opacity,
  };
};

SupportResistanceSeries.prototype.getSegments = function (moreProps, accessor) {
  const {
    plotData,
    xScale,
    xAccessor,
    chartConfig: { yScale },
  } = moreProps;
  const segments = [];

  for (let i = 0; i < plotData.length; i += 1) {
    const datum = plotData[i];
    const value = accessor(datum);
    if (!Number.isFinite(value)) {
      continue;
    }

    const x = xScale(xAccessor(datum));
    const y = yScale(value);

    if (segments.length === 0) {
      segments.push({ type: "M", x, y, rawX: x, rawY: y });
    } else {
      segments.push({ type: "L", x, y, rawX: x, rawY: y });
    }
  }

  return segments;
};

SupportResistanceSeries.prototype.getChannelPath = function (moreProps) {
  const {
    plotData,
    xScale,
    xAccessor,
    chartConfig: { yScale },
  } = moreProps;
  const points = [];

  for (let i = 0; i < plotData.length; i += 1) {
    const datum = plotData[i];
    const support = this.yAccessorSupport(datum);
    const resistance = this.yAccessorResistance(datum);

    if (Number.isFinite(support) && Number.isFinite(resistance)) {
      const x = xScale(xAccessor(datum));
      points.push({
        x,
        support: yScale(support),
        resistance: yScale(resistance),
      });
    }
  }

  if (points.length === 0) return null;

  // Create path: support line forward, then resistance line backward
  let path = `M ${points[0].x} ${points[0].support}`;

  // Draw support line forward
  for (let i = 1; i < points.length; i += 1) {
    path += ` L ${points[i].x} ${points[i].support}`;
  }

  // Draw resistance line backward
  for (let i = points.length - 1; i >= 0; i -= 1) {
    path += ` L ${points[i].x} ${points[i].resistance}`;
  }

  path += " Z"; // Close the path

  return path;
};

SupportResistanceSeries.prototype.renderSVG = function (
  moreProps,
  supportStrokeProps,
  resistanceStrokeProps,
  channelFillProps,
) {
  const supportSegments = this.getSegments(
    moreProps,
    this.yAccessorSupport.bind(this),
  );
  const resistanceSegments = this.getSegments(
    moreProps,
    this.yAccessorResistance.bind(this),
  );

  const buildPath = (segments) => {
    if (!segments.length) return null;
    return segments.map((cmd) => `${cmd.type} ${cmd.x} ${cmd.y}`).join(" ");
  };

  const paths = [];

  // Draw channel fill first (behind the lines)
  const channelPath = this.getChannelPath(moreProps);
  if (channelPath && channelFillProps.opacity > 0) {
    paths.push(
      <path
        key="channel"
        d={channelPath}
        fill={channelFillProps.color}
        fillOpacity={channelFillProps.opacity}
        stroke="none"
      />,
    );
  }

  // Draw support line
  const supportPath = buildPath(supportSegments);
  if (supportPath) {
    paths.push(
      <path
        key="support"
        d={supportPath}
        fill="none"
        stroke={supportStrokeProps.color}
        strokeWidth={supportStrokeProps.width}
        strokeOpacity={supportStrokeProps.opacity}
        strokeDasharray={getStrokeDasharray(supportStrokeProps.dash)}
      />,
    );
  }

  // Draw resistance line
  const resistancePath = buildPath(resistanceSegments);
  if (resistancePath) {
    paths.push(
      <path
        key="resistance"
        d={resistancePath}
        fill="none"
        stroke={resistanceStrokeProps.color}
        strokeWidth={resistanceStrokeProps.width}
        strokeOpacity={resistanceStrokeProps.opacity}
        strokeDasharray={getStrokeDasharray(resistanceStrokeProps.dash)}
      />,
    );
  }

  return <g>{paths}</g>;
};

SupportResistanceSeries.prototype.drawOnCanvas = function (
  ctx,
  moreProps,
  supportStrokeProps,
  resistanceStrokeProps,
  channelFillProps,
) {
  const drawChannelFill = () => {
    const {
      plotData,
      xScale,
      xAccessor,
      chartConfig: { yScale },
    } = moreProps;
    const points = [];

    for (let i = 0; i < plotData.length; i += 1) {
      const datum = plotData[i];
      const support = this.yAccessorSupport(datum);
      const resistance = this.yAccessorResistance(datum);

      if (Number.isFinite(support) && Number.isFinite(resistance)) {
        const x = xScale(xAccessor(datum));
        points.push({
          x,
          support: yScale(support),
          resistance: yScale(resistance),
        });
      }
    }

    if (points.length === 0 || channelFillProps.opacity <= 0) return;

    ctx.beginPath();

    // Start at first support point
    ctx.moveTo(points[0].x, points[0].support);

    // Draw support line forward
    for (let i = 1; i < points.length; i += 1) {
      ctx.lineTo(points[i].x, points[i].support);
    }

    // Draw resistance line backward
    for (let i = points.length - 1; i >= 0; i -= 1) {
      ctx.lineTo(points[i].x, points[i].resistance);
    }

    ctx.closePath();
    ctx.fillStyle = hexToRGBA(channelFillProps.color, channelFillProps.opacity);
    ctx.fill();
  };

  const drawCommands = (segments, stroke) => {
    if (!segments.length) return;
    ctx.beginPath();
    segments.forEach((cmd) => {
      if (cmd.type === "M") {
        ctx.moveTo(cmd.x, cmd.y);
      } else {
        ctx.lineTo(cmd.x, cmd.y);
      }
    });
    const { hovering } = moreProps;
    ctx.lineWidth = hovering ? (stroke.width + 1) * 1.5 : stroke.width;
    ctx.strokeStyle = hexToRGBA(stroke.color, stroke.opacity);
    ctx.setLineDash(getStrokeDasharray(stroke.dash).split(","));
    ctx.stroke();
    ctx.setLineDash([]);
  };

  // Draw channel fill first
  drawChannelFill();

  // Draw support and resistance lines
  drawCommands(
    this.getSegments(moreProps, this.yAccessorSupport.bind(this)),
    supportStrokeProps,
  );
  drawCommands(
    this.getSegments(moreProps, this.yAccessorResistance.bind(this)),
    resistanceStrokeProps,
  );
};

SupportResistanceSeries.prototype.isHover = function (moreProps) {
  const {
    highlightOnHover,
    hoverTolerance,
    onHover,
    onUnHover,
    onContextMenu,
    onDoubleClick,
    onClick,
  } = this.props;

  const needsHover =
    highlightOnHover ||
    onHover ||
    onUnHover ||
    onContextMenu ||
    onDoubleClick ||
    onClick;
  if (!needsHover) return false;

  const tolerance = Number.isFinite(hoverTolerance) ? hoverTolerance : 6;
  const {
    mouseXY,
    chartConfig: { origin },
  } = moreProps;
  if (!mouseXY) return false;

  const [mouseXRaw, mouseYRaw] = mouseXY;
  const pointer = {
    x: mouseXRaw + origin[0],
    y: mouseYRaw + origin[1],
  };

  const checkSegments = (segments) => {
    if (!segments.length) return false;
    for (let i = 0; i < segments.length - 1; i += 1) {
      const start = segments[i];
      const end = segments[i + 1];
      if (!start || !end) continue;

      const sx = (start.rawX ?? start.x) + origin[0];
      const sy = (start.rawY ?? start.y) + origin[1];
      const ex = (end.rawX ?? end.x) + origin[0];
      const ey = (end.rawY ?? end.y) + origin[1];

      const dx = ex - sx;
      const dy = ey - sy;
      const len2 = dx * dx + dy * dy;
      if (len2 === 0) {
        if (Math.hypot(pointer.x - sx, pointer.y - sy) <= tolerance)
          return true;
        continue;
      }

      let t = ((pointer.x - sx) * dx + (pointer.y - sy) * dy) / len2;
      t = Math.max(0, Math.min(1, t));
      const px = sx + t * dx;
      const py = sy + t * dy;
      const distance = Math.hypot(pointer.x - px, pointer.y - py);

      if (distance <= tolerance) return true;
    }
    return false;
  };

  // Check hover for both support and resistance lines
  const supportSegments = this.getSegments(
    moreProps,
    this.yAccessorSupport.bind(this),
  );
  const resistanceSegments = this.getSegments(
    moreProps,
    this.yAccessorResistance.bind(this),
  );

  return checkSegments(supportSegments) || checkSegments(resistanceSegments);
};

SupportResistanceSeries.propTypes = {
  className: PropTypes.string,
  yAccessor: PropTypes.func.isRequired,
  supportStroke: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  resistanceStroke: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  channelFillColor: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  channelFill: PropTypes.bool,
  clip: PropTypes.bool,
  highlightOnHover: PropTypes.bool,
  hoverTolerance: PropTypes.number,
  onHover: PropTypes.func,
  onUnHover: PropTypes.func,
  onClick: PropTypes.func,
  onDoubleClick: PropTypes.func,
  onContextMenu: PropTypes.func,
};

SupportResistanceSeries.defaultProps = {
  className: "react-stockcharts-support-resistance-series",
  clip: true,
  highlightOnHover: false,
  channelFill: true,
  supportStroke: {
    color: "#80c080",
    width: 1,
    dash: "Solid",
    opacity: 1,
  },
  resistanceStroke: {
    color: "#80c080",
    width: 1,
    dash: "Solid",
    opacity: 1,
  },
  channelFillColor: {
    color: "#80c080",
    opacity: 0.2,
  },
};

export default SupportResistanceSeries;
